import { useState, useRef, useEffect } from 'react';
import { rotuloPeriodo } from '../lib/snapshot-types.js';

export interface DateRangeValue {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  preset: PresetId;
}

export type PresetId =
  | 'today' | 'yesterday' | '7d' | '30d' | '90d'
  | 'this_week' | 'last_week'
  | 'this_month' | 'last_month'
  | 'this_quarter' | 'last_quarter'
  | 'this_year' | 'ytd' | 'all' | 'custom';

interface PresetDef {
  id: PresetId;
  label: string;
  compute: () => { from: string; to: string };
}

/** El dia LOCAL de una fecha (con toISOString, una medianoche de Madrid caia en el dia anterior). */
const isoDay = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = (): Date => new Date();
const addDays = (d: Date, n: number): Date => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d: Date): Date => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };
const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfQuarter = (d: Date): Date => { const q = Math.floor(d.getMonth() / 3); return new Date(d.getFullYear(), q * 3, 1); };
const endOfQuarter = (d: Date): Date => { const q = Math.floor(d.getMonth() / 3); return new Date(d.getFullYear(), q * 3 + 3, 0); };
const hoyIso = (): string => isoDay(today());

export const PRESETS: PresetDef[] = [
  { id: 'today',         label: 'Hoy',                compute: () => ({ from: isoDay(today()), to: isoDay(today()) }) },
  { id: 'yesterday',     label: 'Ayer',               compute: () => { const y = addDays(today(), -1); return { from: isoDay(y), to: isoDay(y) }; } },
  { id: '7d',            label: 'Últimos 7 días',     compute: () => ({ from: isoDay(addDays(today(), -6)), to: isoDay(today()) }) },
  { id: '30d',           label: 'Últimos 30 días',    compute: () => ({ from: isoDay(addDays(today(), -29)), to: isoDay(today()) }) },
  { id: '90d',           label: 'Últimos 90 días',    compute: () => ({ from: isoDay(addDays(today(), -89)), to: isoDay(today()) }) },
  // Todo periodo que llega a hoy acaba HOY (como en el servidor, que es quien lo resuelve): con la
  // unidad entera las series caian a 0 hasta fin de mes. Las reuniones por hacer van aparte.
  { id: 'this_week',     label: 'Esta semana',        compute: () => ({ from: isoDay(startOfWeek(today())), to: hoyIso() }) },
  { id: 'last_week',     label: 'Semana pasada',      compute: () => { const s = addDays(startOfWeek(today()), -7); const e = addDays(s, 6); return { from: isoDay(s), to: isoDay(e) }; } },
  { id: 'this_month',    label: 'Este mes',           compute: () => ({ from: isoDay(startOfMonth(today())), to: hoyIso() }) },
  { id: 'last_month',    label: 'Mes pasado',         compute: () => { const s = startOfMonth(addDays(startOfMonth(today()), -1)); const e = endOfMonth(s); return { from: isoDay(s), to: isoDay(e) }; } },
  { id: 'this_quarter',  label: 'Este trimestre',     compute: () => ({ from: isoDay(startOfQuarter(today())), to: hoyIso() }) },
  { id: 'last_quarter',  label: 'Trimestre anterior', compute: () => { const s = startOfQuarter(addDays(startOfQuarter(today()), -1)); const e = endOfQuarter(s); return { from: isoDay(s), to: isoDay(e) }; } },
  { id: 'this_year',     label: 'Este año',           compute: () => ({ from: `${today().getFullYear()}-01-01`, to: hoyIso() }) },
  { id: 'ytd',           label: 'En lo que va de año', compute: () => ({ from: `${today().getFullYear()}-01-01`, to: isoDay(today()) }) },
  { id: 'all',           label: 'Todo',               compute: () => ({ from: '2024-01-01', to: isoDay(today()) }) },
];

/**
 * Los que se ofrecen en el menu (revision del 7-oct: con 14 periodos, «Este año» y «En lo que va de
 * año» solo se distinguian sabiendo como se calculaban). Los demas se siguen entendiendo si llegan
 * (una pagina abierta durante el despliegue), pero no se ofrecen.
 */
export const PRESETS_EN_MENU: readonly PresetId[] = ['today', 'yesterday', '7d', '30d', 'this_month', 'last_month', 'this_quarter'];

export function computePreset(id: PresetId): { from: string; to: string } {
  const p = PRESETS.find((x) => x.id === id);
  if (p) return p.compute();
  return PRESETS.find((x) => x.id === '30d')!.compute();
}

interface Props {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  /**
   * El periodo que de verdad se aplico (lo resuelve el servidor en la zona del
   * inquilino): se lee en el boton ("7 sep - 6 oct") y rellena las fechas a medida.
   */
  periodo?: { desde: string; hasta: string };
}

export function DateRangePicker({ value, onChange, periodo }: Props) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(periodo?.desde ?? value.from);
  const [customTo, setCustomTo] = useState(periodo?.hasta ?? value.to);
  const ref = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const currentPreset = PRESETS.find((p) => p.id === value.preset);
  const label = currentPreset ? currentPreset.label : 'A medida';
  const dias = periodo ? rotuloPeriodo(periodo.desde, periodo.hasta) : value.preset === 'custom' ? rotuloPeriodo(value.from, value.to) : null;

  const abrir = (): void => {
    // Las fechas a medida empiezan en el periodo que se esta viendo.
    if (!open && periodo) { setCustomFrom(periodo.desde); setCustomTo(periodo.hasta); }
    setOpen((o) => !o);
  };

  const pickPreset = (id: PresetId): void => {
    const r = computePreset(id);
    onChange({ ...r, preset: id });
    setOpen(false);
  };

  const applyCustom = (): void => {
    if (customFrom && customTo && customFrom <= customTo) {
      onChange({ from: customFrom, to: customTo, preset: 'custom' });
      setOpen(false);
    }
  };

  return (
    <div
      ref={ref}
      className="range"
      onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); boton.current?.focus(); } }}
    >
      <button ref={boton} type="button" className={`range-trigger ${open ? 'open' : ''}`} aria-haspopup="true" aria-expanded={open} onClick={abrir}>
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className="range-icono">
          <rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M2 6.5h12M5.5 1.8v2.6M10.5 1.8v2.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <span className="range-preset">{label}</span>
        {dias && <span className="range-dias">{dias}</span>}
        <svg className="range-flecha" viewBox="0 0 10 10" width="9" height="9" aria-hidden="true"><path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div className="range-menu" role="group" aria-label="Periodo">
          {PRESETS.filter((p) => PRESETS_EN_MENU.includes(p.id)).map((p) => (
            <button
              key={p.id}
              type="button"
              className={value.preset === p.id ? 'active' : ''}
              aria-pressed={value.preset === p.id}
              onClick={() => pickPreset(p.id)}
            >{p.label}</button>
          ))}
          <div className="custom-range">
            <input type="date" aria-label="Desde" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <input type="date" aria-label="Hasta" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            <button type="button" className="apply" onClick={applyCustom}>Aplicar</button>
          </div>
        </div>
      )}
    </div>
  );
}
